import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ApiError, api } from '../api/client';
import { PersonIcon } from '../components/AuthIcons';
import { AuthField, AuthFieldSlot, AuthPasswordField } from '../components/AuthField';
import { PasswordValidationRules } from '../components/PasswordValidationRules';
import {
  useAuthCardMotion,
  authFieldGroupVariants,
  authFieldVariants,
} from '../components/AuthSplitShell';
import { SelectMenu } from '../components/SelectMenu';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { readSignupAccountType, withNextParam } from '../lib/nextPath';
import { websiteUrl } from '../lib/websiteUrl';
import { isValidTaxId, normalizeTaxId } from '../lib/taxId';
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  formatNationalDisplay,
  isPhoneComplete,
  normalizeNationalInput,
  phonePlaceholderDisplay,
  toE164,
} from '../lib/phoneCountries';
import { MIN_PASSWORD_LENGTH, displayPasswordRulesMet, isStrongPassword, normalizePasswordInput } from '../lib/password';

type AccountType = 'individual' | 'b2b';

function MotionField({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div className={className} variants={authFieldVariants}>
      {children}
    </motion.div>
  );
}

type FieldErrorTarget =
  | 'email'
  | 'displayName'
  | 'orgName'
  | 'taxId'
  | 'phone'
  | 'password'
  | 'confirm'
  | 'terms'
  | 'form';

type SignUpErrorKey =
  | 'errEmailRequired'
  | 'errEmailTaken'
  | 'errContactNameRequired'
  | 'errOrgNameRequired'
  | 'errInvalidTaxId'
  | 'errPhoneRequired'
  | 'errInvalidPhone'
  | 'errPhoneTaken'
  | 'errPasswordRequired'
  | 'errWeakPassword'
  | 'errPasswordMismatch'
  | 'errTermsNotAccepted'
  | 'errTooMany'
  | 'errGeneric';

type FieldError = {
  field: FieldErrorTarget;
  key: SignUpErrorKey;
};

type EmailAvailability = 'idle' | 'checking' | 'available' | 'taken';

// Mirrors control-api's PromoCodeStatus, plus 'idle'/'checking' for the
// in-flight states — see llmr-control-api's own comment on why the amount
// is never part of this answer.
type PromoCodeCheck = 'idle' | 'checking' | 'valid' | 'not_found' | 'used' | 'not_yet_valid' | 'expired';

// Stable, language-independent values sent as `referral_source` — the
// dropdown's labels are localized (dict.signUp.referralSourceOptions), but
// staff reading this column later must see the same value regardless of
// which locale the customer signed up in. 'other' is replaced by whatever
// free text the customer types in that case (see referralSourceToSend).
const REFERRAL_SOURCE_OPTIONS = [
  'ai_expo_2026',
  'facebook',
  'instagram',
  'line',
  'friend',
  'google_search',
  'other',
] as const;
type ReferralSource = (typeof REFERRAL_SOURCE_OPTIONS)[number];
// '' means "cleared" — the customer opened the clear (×) button because they
// don't want to answer. Distinct from never having touched the field, which
// starts at REFERRAL_SOURCE_DEFAULT below; both send nothing to the server.
type ReferralSourceValue = ReferralSource | '';
const REFERRAL_SOURCE_DEFAULT: ReferralSourceValue = 'ai_expo_2026';

function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

// '' (cleared, or never touched away from empty) sends nothing — the field
// is optional and omitting it is how "the customer chose not to answer"
// gets represented. 'other' sends whatever free text the customer typed
// (falling back to the literal word so a deliberate "other" pick is never
// empty-but-not-null) — every other option sends its stable key as-is.
function referralSourceToSend(source: ReferralSourceValue, otherText: string): string | undefined {
  if (source === '') return undefined;
  if (source !== 'other') return source;
  return otherText.trim() || 'other';
}

/**
 * Self-service sign-up — individual or organization (b2b). Staff accounts
 * remain admin-created only. accountType 'b2b' adds orgName + taxId to the
 * request and relabels the email/displayName fields (same two fields either
 * way: "business email" is still just `email`, "contact name" is still just
 * `display_name` — the whole app already keeps a person's own name and their
 * organization's name as fully separate things, see OrgSwitcher/NavUserMenu).
 *
 * Never establishes a session: the contract answers 202 on success, and the
 * account stays unverified until the emailed link is followed (/auth/login
 * refuses it until then). The organization itself (individual or b2b) isn't
 * created by this call either — only once that link is followed, same as it
 * always has been for individual. A duplicate email is refused with 409.
 */
export function SignUp() {
  const { dict, locale } = useLanguage();
  const location = useLocation();
  const loginHref = withNextParam('/login', location.search);
  const site = websiteUrl();
  const localePrefix = locale === 'th' ? 'th/' : '';
  const termsHref = site ? `${site}${localePrefix}terms/` : null;
  const privacyHref = site ? `${site}${localePrefix}privacy/` : null;
  const [accountType, setAccountType] = useState<AccountType>(() =>
    readSignupAccountType(location.search),
  );

  useEffect(() => {
    setAccountType(readSignupAccountType(location.search));
  }, [location.search]);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY.iso2);
  const [phoneNational, setPhoneNational] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [fieldError, setFieldError] = useState<FieldError | null>(null);
  const [emailAvailability, setEmailAvailability] = useState<EmailAvailability>('idle');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'busy' | 'sent'>('idle');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeCheck, setPromoCodeCheck] = useState<PromoCodeCheck>('idle');
  const [referralSource, setReferralSource] = useState<ReferralSourceValue>(REFERRAL_SOURCE_DEFAULT);
  const [referralSourceOther, setReferralSourceOther] = useState('');
  const emailCheckRef = useRef<AbortController | null>(null);
  const promoCheckRef = useRef<AbortController | null>(null);

  const isOrg = accountType === 'b2b';
  const phone = isPhoneComplete(phoneCountry, phoneNational)
    ? toE164(phoneCountry, phoneNational)
    : null;
  const phoneLooksInvalid = phoneNational !== '' && phone === null;
  const passwordsMatch = confirmPassword === '' || confirmPassword === password;
  const passwordStrong = isStrongPassword(password);
  const passwordInvalid = password !== '' && !passwordStrong;
  const taxIdLooksInvalid = isOrg && taxId !== '' && !isValidTaxId(taxId);
  const orgFieldsValid = !isOrg || (displayName.trim() !== '' && orgName.trim() !== '' && isValidTaxId(taxId));
  // Only a DEFINITE negative blocks submit — 'idle'/'checking'/'valid' all
  // let it through, same reasoning as email: don't punish someone who never
  // blurred the field, or whose check is still in flight.
  const promoCodeBad =
    promoCodeCheck === 'not_found' ||
    promoCodeCheck === 'used' ||
    promoCodeCheck === 'not_yet_valid' ||
    promoCodeCheck === 'expired';
  const canSubmit =
    !busy &&
    orgFieldsValid &&
    passwordStrong &&
    password === confirmPassword &&
    confirmPassword !== '' &&
    acceptedTerms &&
    !promoCodeBad;

  function selectAccountType(next: AccountType) {
    setAccountType(next);
    clearFieldError('displayName');
    clearFieldError('orgName');
    clearFieldError('taxId');
    if (next === 'individual') {
      setOrgName('');
      setTaxId('');
    }
  }

  function errorText(key: SignUpErrorKey): string {
    return dict.signUp[key];
  }

  function validationMessage(): FieldError | null {
    if (!email.trim()) {
      return { field: 'email', key: 'errEmailRequired' };
    }
    if (emailAvailability === 'taken') {
      return { field: 'email', key: 'errEmailTaken' };
    }
    if (isOrg && !displayName.trim()) {
      return { field: 'displayName', key: 'errContactNameRequired' };
    }
    if (isOrg && !orgName.trim()) {
      return { field: 'orgName', key: 'errOrgNameRequired' };
    }
    if (isOrg && !isValidTaxId(taxId)) {
      return { field: 'taxId', key: 'errInvalidTaxId' };
    }
    if (!phone) {
      return {
        field: 'phone',
        key: phoneNational === '' ? 'errPhoneRequired' : 'errInvalidPhone',
      };
    }
    if (password === '') {
      return { field: 'password', key: 'errPasswordRequired' };
    }
    const normalizedPassword = normalizePasswordInput(password);
    const normalizedConfirm = normalizePasswordInput(confirmPassword);
    if (!isStrongPassword(normalizedPassword)) {
      return { field: 'password', key: 'errWeakPassword' };
    }
    if (normalizedPassword !== normalizedConfirm) {
      return { field: 'confirm', key: 'errPasswordMismatch' };
    }
    if (!acceptedTerms) {
      return { field: 'terms', key: 'errTermsNotAccepted' };
    }
    return null;
  }

  function clearFieldError(field?: FieldErrorTarget) {
    setFieldError((current) => {
      if (!current) return null;
      if (!field || current.field === field) return null;
      return current;
    });
  }

  function showFieldError(next: FieldError) {
    setFieldError(next);
  }

  function resetEmailAvailability() {
    emailCheckRef.current?.abort();
    emailCheckRef.current = null;
    setEmailAvailability('idle');
  }

  async function checkEmailAvailability(): Promise<EmailAvailability> {
    const trimmed = email.trim();
    if (!looksLikeEmail(trimmed)) {
      resetEmailAvailability();
      return 'idle';
    }

    emailCheckRef.current?.abort();
    const controller = new AbortController();
    emailCheckRef.current = controller;

    setEmailAvailability('checking');
    clearFieldError('email');

    try {
      await api.checkRegisterEmail(trimmed, controller.signal);
      if (controller.signal.aborted) return 'idle';
      setEmailAvailability('available');
      return 'available';
    } catch (err) {
      if (controller.signal.aborted) return 'idle';
      if (err instanceof ApiError && (err.is('/problems/email-already-registered') || err.status === 409)) {
        setEmailAvailability('taken');
        showFieldError({ field: 'email', key: 'errEmailTaken' });
        return 'taken';
      }
      setEmailAvailability('idle');
      return 'idle';
    }
  }

  function resetPromoCodeCheck() {
    promoCheckRef.current?.abort();
    promoCheckRef.current = null;
    setPromoCodeCheck('idle');
  }

  async function checkPromoCodeValidity() {
    const trimmed = promoCode.trim().toUpperCase();
    if (trimmed === '') {
      resetPromoCodeCheck();
      return;
    }

    promoCheckRef.current?.abort();
    const controller = new AbortController();
    promoCheckRef.current = controller;

    setPromoCodeCheck('checking');
    try {
      const res = await api.checkRegisterPromoCode(trimmed, controller.signal);
      if (controller.signal.aborted) return;
      setPromoCodeCheck(res.status);
    } catch {
      if (controller.signal.aborted) return;
      // A network hiccup here must not block signup over a bonus feature —
      // fall back to 'idle' (submit allowed) rather than treating it as bad.
      setPromoCodeCheck('idle');
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    let availability = emailAvailability;
    if (availability !== 'available' && looksLikeEmail(email)) {
      availability = await checkEmailAvailability();
    }
    if (availability === 'taken') {
      showFieldError({ field: 'email', key: 'errEmailTaken' });
      return;
    }
    const message = validationMessage();
    if (message) {
      showFieldError(message);
      return;
    }
    if (!phone) {
      showFieldError({ field: 'phone', key: 'errInvalidPhone' });
      return;
    }
    setBusy(true);
    setFieldError(null);
    try {
      const normalizedPassword = normalizePasswordInput(password);
      if (normalizedPassword !== password) setPassword(normalizedPassword);
      if (normalizePasswordInput(confirmPassword) !== confirmPassword) {
        setConfirmPassword(normalizePasswordInput(confirmPassword));
      }
      await api.register(
        email,
        normalizedPassword,
        phone,
        acceptedTerms,
        displayName.trim(),
        accountType,
        isOrg ? orgName.trim() : undefined,
        isOrg ? normalizeTaxId(taxId) : undefined,
        promoCode.trim() || undefined,
        referralSourceToSend(referralSource, referralSourceOther),
      );
      setPending(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        showFieldError({ field: 'form', key: 'errTooMany' });
      } else if (err instanceof ApiError && err.is('/problems/weak-password')) {
        showFieldError({ field: 'password', key: 'errWeakPassword' });
      } else if (err instanceof ApiError && err.is('/problems/org-name-required')) {
        showFieldError({ field: 'orgName', key: 'errOrgNameRequired' });
      } else if (err instanceof ApiError && err.is('/problems/invalid-tax-id')) {
        showFieldError({ field: 'taxId', key: 'errInvalidTaxId' });
      } else if (err instanceof ApiError && err.is('/problems/invalid-phone')) {
        showFieldError({ field: 'phone', key: 'errInvalidPhone' });
      } else if (err instanceof ApiError && err.is('/problems/phone-taken')) {
        showFieldError({ field: 'phone', key: 'errPhoneTaken' });
      } else if (err instanceof ApiError && err.is('/problems/terms-not-accepted')) {
        showFieldError({ field: 'terms', key: 'errTermsNotAccepted' });
      } else if (
        err instanceof ApiError &&
        (err.is('/problems/email-already-registered') || err.status === 409)
      ) {
        setEmailAvailability('taken');
        showFieldError({ field: 'email', key: 'errEmailTaken' });
      } else {
        showFieldError({ field: 'form', key: 'errGeneric' });
      }
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setResendState('busy');
    try {
      await api.resendVerification(email);
    } finally {
      // Always answers 202 — there is nothing to branch on here either.
      setResendState('sent');
    }
  }

  const emailMessage =
    fieldError?.field === 'email'
      ? errorText(fieldError.key)
      : emailAvailability === 'checking'
        ? dict.signUp.errCheckingEmail
        : emailAvailability === 'taken'
          ? dict.signUp.errEmailTaken
          : null;
  const displayNameMessage = fieldError?.field === 'displayName' ? errorText(fieldError.key) : null;
  const orgNameMessage = fieldError?.field === 'orgName' ? errorText(fieldError.key) : null;
  const taxIdMessage =
    fieldError?.field === 'taxId'
      ? errorText(fieldError.key)
      : taxIdLooksInvalid
        ? dict.signUp.errInvalidTaxId
        : null;
  const phoneMessage =
    fieldError?.field === 'phone'
      ? errorText(fieldError.key)
      : phoneLooksInvalid
        ? dict.signUp.errInvalidPhone
        : null;
  const passwordMessage =
    fieldError?.field === 'password'
      ? errorText(fieldError.key)
      : password !== '' && displayPasswordRulesMet(password) && !passwordStrong
        ? dict.signUp.errWeakPassword
        : null;
  const confirmMessage =
    fieldError?.field === 'confirm'
      ? errorText(fieldError.key)
      : !passwordsMatch
        ? dict.signUp.errPasswordMismatch
        : null;
  const promoCodeMessage =
    promoCodeCheck === 'checking'
      ? dict.signUp.errCheckingPromoCode
      : promoCodeCheck === 'not_found'
        ? dict.signUp.errPromoCodeNotFound
        : promoCodeCheck === 'used'
          ? dict.signUp.errPromoCodeUsed
          : promoCodeCheck === 'not_yet_valid'
            ? dict.signUp.errPromoCodeNotYetValid
            : promoCodeCheck === 'expired'
              ? dict.signUp.errPromoCodeExpired
              : null;

  const cardMotion = useAuthCardMotion(pending ? 'signup-pending' : 'signup-form', true);

  return (
    <AnimatePresence mode="wait">
      {pending ? (
        <motion.div
          key="pending"
          className="auth-split-form-inner"
          {...cardMotion}
        >
          <motion.h1 variants={authFieldVariants}>{dict.signUp.pendingTitle}</motion.h1>
          <motion.p className="auth-lede" variants={authFieldVariants}>
            {dict.signUp.pendingBody(email)}
          </motion.p>

          <MotionField>
            <button
              type="button"
              className="ghost btn-block"
              disabled={resendState === 'busy'}
              onClick={() => void resend()}
            >
              {resendState === 'busy'
                ? dict.signUp.resendBusy
                : resendState === 'sent'
                  ? dict.signUp.resendSent
                  : dict.signUp.resendButton}
            </button>
          </MotionField>

          <motion.p className="note" variants={authFieldVariants}>
            {dict.signUp.haveAccount} <Link to={loginHref}>{dict.signUp.signInLink}</Link>
          </motion.p>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          className="auth-split-form-inner"
          onSubmit={submit}
          {...cardMotion}
        >
          <motion.h1 variants={authFieldVariants}>{dict.signUp.title}</motion.h1>
          <motion.div className="auth-split-form-scroll" variants={authFieldGroupVariants}>
            <motion.p className="auth-lede" variants={authFieldVariants}>
              {dict.signUp.lede}
            </motion.p>
            {fieldError?.field === 'form' && (
              <MotionField>
                <div className="err">{errorText(fieldError.key)}</div>
              </MotionField>
            )}

            <MotionField>
              <AuthFieldSlot id="signup-account-type-label" label={dict.signUp.accountTypeLabel}>
                <div className="account-type-toggle" role="radiogroup" aria-labelledby="signup-account-type-label">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!isOrg}
                    className={`account-type-toggle-btn${!isOrg ? ' is-active' : ''}`}
                    onClick={() => selectAccountType('individual')}
                  >
                    {dict.signUp.accountTypeIndividual}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isOrg}
                    className={`account-type-toggle-btn${isOrg ? ' is-active' : ''}`}
                    onClick={() => selectAccountType('b2b')}
                  >
                    {dict.signUp.accountTypeOrganization}
                  </button>
                </div>
              </AuthFieldSlot>
            </MotionField>

            <MotionField>
              <AuthField
                id="email"
                label={isOrg ? dict.signUp.emailLabelOrganization : dict.signUp.emailLabel}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  resetEmailAvailability();
                  clearFieldError('email');
                }}
                onBlur={() => void checkEmailAvailability()}
                autoComplete="email"
                autoFocus
                invalid={emailAvailability === 'taken' || fieldError?.field === 'email'}
                trailing={<PersonIcon />}
                message={
                  emailMessage ? (
                    <p className={emailAvailability === 'checking' ? 'note' : 'note note-warn'}>{emailMessage}</p>
                  ) : null
                }
              />
            </MotionField>

            <MotionField>
              <AuthField
                id="displayName"
                label={isOrg ? dict.signUp.displayNameLabelOrganization : dict.signUp.displayNameLabel}
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                  clearFieldError('displayName');
                }}
                required={isOrg}
                invalid={fieldError?.field === 'displayName'}
                autoComplete="name"
                message={displayNameMessage ? <p className="note note-warn">{displayNameMessage}</p> : null}
              />
            </MotionField>

            {isOrg && (
              <>
                <MotionField>
                  <AuthField
                    id="orgName"
                    label={dict.signUp.orgNameLabel}
                    value={orgName}
                    onChange={(e) => {
                      setOrgName(e.target.value);
                      clearFieldError('orgName');
                    }}
                    required
                    invalid={fieldError?.field === 'orgName'}
                    autoComplete="organization"
                    message={orgNameMessage ? <p className="note note-warn">{orgNameMessage}</p> : null}
                  />
                </MotionField>

                <MotionField>
                  <AuthField
                    id="taxId"
                    label={dict.signUp.taxIdLabel}
                    value={taxId}
                    onChange={(e) => {
                      setTaxId(e.target.value.replace(/\D/g, '').slice(0, 13));
                      clearFieldError('taxId');
                    }}
                    inputMode="numeric"
                    maxLength={13}
                    required
                    invalid={taxIdLooksInvalid || fieldError?.field === 'taxId'}
                    message={
                      <p className={taxIdMessage ? 'note note-warn' : 'note'}>{taxIdMessage ?? dict.signUp.taxIdHint}</p>
                    }
                  />
                </MotionField>
              </>
            )}

            <MotionField>
              <AuthFieldSlot
                id="signup-phone-label"
                label={dict.signUp.phoneLabel}
                className="auth-field--phone"
                message={phoneMessage ? <p className="note note-warn">{phoneMessage}</p> : null}
              >
                <div className="phone-row">
                  <SelectMenu
                    id="countryCode"
                    aria-label={dict.signUp.countryCodeLabel}
                    value={phoneCountry}
                    className="phone-country-select"
                    onChange={(next) => {
                      setPhoneCountry(next);
                      setPhoneNational((prev) => normalizeNationalInput(next, prev));
                      clearFieldError('phone');
                    }}
                    options={PHONE_COUNTRIES.map((c) => ({ value: c.iso2, label: c.label }))}
                  />
                  <input
                    id="phone"
                    type="tel"
                    value={formatNationalDisplay(phoneCountry, phoneNational)}
                    onChange={(e) => {
                      setPhoneNational(normalizeNationalInput(phoneCountry, e.target.value));
                      clearFieldError('phone');
                    }}
                    placeholder={phonePlaceholderDisplay(phoneCountry)}
                    autoComplete="tel-national"
                    className="auth-field-input phone-national-input"
                    aria-labelledby="signup-phone-label"
                    aria-invalid={phoneMessage !== null}
                  />
                </div>
              </AuthFieldSlot>
            </MotionField>

            <MotionField>
              <AuthPasswordField
                id="password"
                label={dict.signUp.passwordLabel}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  clearFieldError('password');
                }}
                autoComplete="new-password"
                minLength={MIN_PASSWORD_LENGTH}
                invalid={passwordInvalid || fieldError?.field === 'password'}
                aria-describedby={password !== '' ? 'signup-password-rules' : undefined}
                inputClassName={passwordInvalid ? 'password-invalid' : ''}
                showPassword={showPassword}
                onToggleShow={() => setShowPassword(!showPassword)}
                showLabel={dict.signUp.showPassword}
                hideLabel={dict.signUp.hidePassword}
                message={
                  <>
                    <PasswordValidationRules password={password} id="signup-password-rules" />
                    {passwordMessage ? <p className="note note-warn">{passwordMessage}</p> : null}
                  </>
                }
              />
            </MotionField>

            <MotionField>
              <AuthPasswordField
                id="confirmPassword"
                label={dict.signUp.confirmPasswordLabel}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  clearFieldError('confirm');
                }}
                autoComplete="new-password"
                invalid={confirmMessage !== null}
                showPassword={showConfirmPassword}
                onToggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
                showLabel={dict.signUp.showPassword}
                hideLabel={dict.signUp.hidePassword}
                message={confirmMessage ? <p className="note note-warn">{confirmMessage}</p> : null}
              />
            </MotionField>

            <MotionField>
              <AuthField
                id="promoCode"
                label={dict.signUp.promoCodeLabel}
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  resetPromoCodeCheck();
                }}
                onBlur={() => void checkPromoCodeValidity()}
                maxLength={8}
                autoComplete="off"
                autoCapitalize="characters"
                invalid={promoCodeBad}
                message={
                  <p className={promoCodeCheck === 'checking' ? 'note' : promoCodeBad ? 'note note-warn' : 'note'}>
                    {promoCodeMessage ?? dict.signUp.promoCodeHint}
                  </p>
                }
              />
            </MotionField>

            <MotionField>
              <AuthFieldSlot
                id="signup-referral-label"
                label={dict.signUp.referralSourceLabel}
                className="auth-field--referral"
              >
                <SelectMenu
                  id="referralSource"
                  aria-label={dict.signUp.referralSourceLabel}
                  className="referral-source-select"
                  searchable
                  clearLabel={dict.common.clear}
                  value={referralSource}
                  onChange={(next) => setReferralSource(next as ReferralSourceValue)}
                  options={REFERRAL_SOURCE_OPTIONS.map((value) => ({
                    value,
                    label: {
                      ai_expo_2026: dict.signUp.referralSourceOptions.aiExpo,
                      facebook: dict.signUp.referralSourceOptions.facebook,
                      instagram: dict.signUp.referralSourceOptions.instagram,
                      line: dict.signUp.referralSourceOptions.line,
                      friend: dict.signUp.referralSourceOptions.friend,
                      google_search: dict.signUp.referralSourceOptions.googleSearch,
                      other: dict.signUp.referralSourceOptions.other,
                    }[value],
                  }))}
                />
                {referralSource === 'other' && (
                  <input
                    value={referralSourceOther}
                    onChange={(e) => setReferralSourceOther(e.target.value)}
                    placeholder={dict.signUp.referralSourceOtherPlaceholder}
                    className="auth-field-input"
                    aria-label={dict.signUp.referralSourceOtherPlaceholder}
                  />
                )}
              </AuthFieldSlot>
            </MotionField>

            <MotionField>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  required
                  aria-invalid={fieldError?.field === 'terms'}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    clearFieldError('terms');
                  }}
                />
                {dict.signUp.agreePrefix}{' '}
                {termsHref ? (
                  <a href={termsHref} target="_blank" rel="noopener noreferrer">{dict.signUp.agreeTerms}</a>
                ) : (
                  dict.signUp.agreeTerms
                )}{' '}
                {dict.signUp.agreeAnd}{' '}
                {privacyHref ? (
                  <a href={privacyHref} target="_blank" rel="noopener noreferrer">{dict.signUp.agreePrivacy}</a>
                ) : (
                  dict.signUp.agreePrivacy
                )}
              </label>
              {fieldError?.field === 'terms' && (
                <p className="note note-warn">{errorText(fieldError.key)}</p>
              )}
            </MotionField>

            <MotionField>
              <button type="submit" disabled={!canSubmit} className="btn-block">
                {busy ? dict.signUp.creatingAccount : dict.signUp.createAccount}
              </button>
            </MotionField>

            <motion.p className="note" variants={authFieldVariants}>
              {dict.signUp.haveAccount} <Link to={loginHref}>{dict.signUp.signInLink}</Link>
            </motion.p>
          </motion.div>
        </motion.form>
      )}
    </AnimatePresence>
  );
}
